import { i18n } from "@/i18n"
import LocalDiffuserDataService from "@/services/LocalDiffuserDataService"
import { useEstablishmentStore } from "@/stores/establishment"
import { normalizeSolicitationType, SolicitationState, SolicitationType, isCmajSolicitationType } from "@/static/Solicitation"
import type { IDestinationProvider } from "@/types/DestinationProvider.types"
import type { IEstablishment } from "@/types/Establishment.types"
import type { IApiSolicitationItem, ISolicitationItem } from "@/types/Solicitation.types"
import type { INotificationCenterAppNotificationItem, INotificationCenterSolicitationItem } from "@/types/NotificationCenter.types"
import type { INotification } from "@/types/Notifications.types"
import { NotificationType } from "@/types/Notifications.types"
import {
  findDialogueDestinationProviderForSolicitation,
  getDialogueSolicitationExternalIds,
} from "@/utils/dialogueSolicitations"

const ATTRIBUTE_NOTIFICATION_TYPES = new Set<NotificationType>([
  NotificationType.NEW_ATTRIBUTE,
  NotificationType.REMOVE_ATTRIBUTE,
])

const NOTIFICATION_CENTER_DEFAULT_PATHS: Partial<Record<NotificationType, string>> = {
  [NotificationType.GOOGLE_MISSING_PERMISSION]: "establishment-listing",
  [NotificationType.FACEBOOK_MISSING_PERMISSION]: "establishment-listing",
  [NotificationType.SYNC_GOOGLE_ADDRESS]: "establishment",
  [NotificationType.TIC]: "dialogue-home",
}

export type NotificationCenterRouteTarget = {
  name: string
  params?: Record<string, string>
  hash?: string
}

/** États archivés ou terminés — ne doivent pas apparaître dans le centre de notifications. */
const EXCLUDED_NOTIFICATION_CENTER_SOLICITATION_STATES = new Set<SolicitationState>([
  SolicitationState.SENT_APIDAE,
  SolicitationState.TREATED,
])

export function isEligibleNotificationCenterSolicitation(apiItem: IApiSolicitationItem): boolean {
  const state = apiItem.state as SolicitationState

  if (EXCLUDED_NOTIFICATION_CENTER_SOLICITATION_STATES.has(state)) {
    return false
  }

  const token = apiItem.token?.trim()
  return Boolean(token)
}

export function normalizeApiSolicitationToItem(apiItem: IApiSolicitationItem): ISolicitationItem {
  const solicitationType = normalizeSolicitationType(apiItem.type)

  return {
    id: apiItem.id ?? apiItem.provider?.id,
    type: solicitationType,
    state: apiItem.state as SolicitationState,
    token: apiItem.token ?? "",
    logo: apiItem.logo ?? "",
    created_at: apiItem.created_at ?? apiItem.sentAt ?? "",
    answeredAt: apiItem.answeredAt ?? "",
    organism: apiItem.account_name ?? apiItem.title ?? "",
    account_id: apiItem.account_id,
    aspect: apiItem.aspect ?? null,
    provider_external_id: apiItem.provider_external_id,
    provider: apiItem.provider,
  }
}

export function mapDestinationSolicitationToCenterItem(
  solicitation: ISolicitationItem,
  establishmentId: number,
  establishmentName: string
): INotificationCenterSolicitationItem {
  const tokenPart = solicitation.token || String(solicitation.id ?? "unknown")

  return {
    id: `sol-${tokenPart}`,
    kind: "solicitation",
    establishmentId,
    establishmentName,
    // Les sollicitations n'ont pas de statut lu/non-lu (réservé aux notifications).
    read: true,
    archived: false,
    solicitation,
  }
}

export type SolicitationCenterFetchContext = {
  establishments: IEstablishment[]
  destinationProviders?: IDestinationProvider[]
  /** null = toutes les fiches / OT visibles (centre de notifications pilotage). */
  ticId?: number | null
  /** Offices de tourisme à interroger (centre de notifications Dialogue = tous les OT). */
  ticIds?: number[]
  /** Espace Dialogue : n'utilise jamais l'endpoint legacy solicitations/{id}. */
  isDialogue?: boolean
}

function buildEstablishmentByExternalIdMap(
  establishments: IEstablishment[]
): Map<number, { establishmentId: number; establishmentName: string }> {
  const map = new Map<number, { establishmentId: number; establishmentName: string }>()

  establishments.forEach((establishment) => {
    ;(establishment.local_diffusers ?? []).forEach((localDiffuser) => {
      const externalId = Number(localDiffuser.external_id)
      if (!Number.isFinite(externalId)) {
        return
      }

      map.set(externalId, {
        establishmentId: establishment.id,
        establishmentName: establishment.name,
      })
    })
  })

  return map
}

function collectSolicitationExternalIds(
  establishments: IEstablishment[],
  destinationProviders: IDestinationProvider[],
  ticId: number | null
): number[] {
  const externalIds = new Set<number>()

  buildEstablishmentByExternalIdMap(establishments).forEach((_value, externalId) => {
    externalIds.add(externalId)
  })

  getDialogueSolicitationExternalIds(destinationProviders, ticId).forEach((externalId) => {
    externalIds.add(externalId)
  })

  return [...externalIds]
}

function resolveSolicitationCenterContext(
  solicitation: ISolicitationItem,
  externalId: number,
  establishmentByExternalId: Map<number, { establishmentId: number; establishmentName: string }>,
  destinationProviders: IDestinationProvider[],
  ticId: number | null
): { establishmentId: number; establishmentName: string } | null {
  if (destinationProviders.length > 0) {
    const destinationProvider = findDialogueDestinationProviderForSolicitation(
      solicitation,
      destinationProviders,
      ticId
    )

    if (destinationProvider) {
      return {
        establishmentId: destinationProvider.establishment_id ?? 0,
        establishmentName: destinationProvider.name,
      }
    }
  }

  const establishment = establishmentByExternalId.get(externalId)
  if (establishment) {
    return {
      establishmentId: establishment.establishmentId,
      establishmentName: establishment.establishmentName,
    }
  }

  if (isCmajSolicitationType(solicitation.type)) {
    const establishmentName =
      solicitation.title?.trim() ||
      solicitation.account_name?.trim() ||
      solicitation.organism?.trim()

    if (establishmentName) {
      return {
        establishmentId: 0,
        establishmentName,
      }
    }
  }

  return null
}

function resolveEstablishmentId(data: INotification["data"]): number {
  const rawId = data.establishmentId
  if (rawId === null || rawId === undefined || rawId === "") {
    return 0
  }
  return Number(rawId)
}

function extractEstablishmentNameFromContent(content: string): string | null {
  const match = content.match(/établissement (.+?)\./i)
  return match?.[1]?.trim() ?? null
}

function resolveEstablishmentName(
  establishmentId: number,
  data: INotification["data"],
  establishmentNames: Map<number, string>
): string {
  if (establishmentId > 0) {
    const knownName = establishmentNames.get(establishmentId)
    if (knownName) {
      return knownName
    }
  }

  const extractedName = extractEstablishmentNameFromContent(data.content)
  if (extractedName) {
    return extractedName
  }

  if (establishmentId > 0) {
    return `Établissement #${establishmentId}`
  }

  return "Établissement"
}

function resolveNotificationTitle(data: INotification["data"]): string {
  const titleKey = data.title
  if (!titleKey) {
    return ""
  }

  const i18nKey = `common.notifications.${titleKey}`
  if (i18n.global.te(i18nKey)) {
    return i18n.global.t(i18nKey)
  }

  return titleKey
}

function extractHrefFromHtml(html: string): string | null {
  const match = html.match(/href=["']([^"']+)["']/i)
  return match?.[1]?.trim() ?? null
}

export function resolveInternalRouteFromLink(link: string): NotificationCenterRouteTarget | null {
  try {
    const url = new URL(link, "https://app.uska.fr")
    const { pathname, hash } = url

    if (pathname === "/establishments/listing" || pathname.startsWith("/establishments/listing/")) {
      const segments = pathname.split("/").filter(Boolean)
      const diffuserType = segments[2]

      return {
        name: "establishment-listing",
        params: diffuserType ? { diffuserType: diffuserType.toUpperCase() } : undefined,
      }
    }

    if (pathname === "/establishment/open-form" || pathname === "/establishment/open-form/") {
      return { name: "dialogue-home", hash: hash || undefined }
    }

    if (pathname === "/establishment" || pathname === "/establishment/") {
      return { name: "establishment", hash: hash || undefined }
    }

    if (pathname === "/solicitations" || pathname === "/solicitations/") {
      return { name: "solicitations" }
    }

    return null
  } catch {
    return null
  }
}

function resolveLink(data: INotification["data"]): string | null {
  if (data.link) {
    return dataLinkOrNull(data.link)
  }

  return extractHrefFromHtml(data.content ?? "")
}

function resolvePathName(notificationType: NotificationType, data: INotification["data"]): string | null {
  if (data.pathName) {
    return data.pathName
  }

  if (ATTRIBUTE_NOTIFICATION_TYPES.has(notificationType)) {
    return "establishment"
  }

  const defaultPath = NOTIFICATION_CENTER_DEFAULT_PATHS[notificationType]
  if (defaultPath) {
    return defaultPath
  }

  const link = resolveLink(data)
  if (link) {
    return resolveInternalRouteFromLink(link)?.name ?? null
  }

  return null
}

export function mapAppNotificationToCenterItem(
  notification: INotification,
  establishmentNames: Map<number, string>
): INotificationCenterAppNotificationItem {
  const establishmentId = resolveEstablishmentId(notification.data)

  return {
    id: notification.id,
    kind: "app_notification",
    establishmentId,
    establishmentName: resolveEstablishmentName(establishmentId, notification.data, establishmentNames),
    title: resolveNotificationTitle(notification.data),
    content: notification.data.content ?? "",
    createdAt: notification.created_at,
    read: notification.read_at !== null,
    archived: false,
    notificationType: notification.type,
    pathName: resolvePathName(notification.type, notification.data),
    link: resolveLink(notification.data),
    externalId: notification.data.external_id ?? null,
  }
}

function dataLinkOrNull(link: string | null | undefined): string | null {
  if (!link) {
    return null
  }
  return link
}

function isNotificationForExistingEstablishment(
  notification: INotification,
  validEstablishmentIds: Set<number>
): boolean {
  const establishmentId = resolveEstablishmentId(notification.data)

  if (establishmentId <= 0) {
    return true
  }

  return validEstablishmentIds.has(establishmentId)
}

export function mapAppNotificationsToCenterItems(
  notifications: INotification[],
  establishmentNames: Map<number, string>
): INotificationCenterAppNotificationItem[] {
  const validEstablishmentIds = new Set(establishmentNames.keys())

  return notifications
    .filter((notification) => notification.type !== NotificationType.SOLICITATION)
    .filter((notification) => isNotificationForExistingEstablishment(notification, validEstablishmentIds))
    .map((notification) => mapAppNotificationToCenterItem(notification, establishmentNames))
}

function appendSolicitationCenterItemsFromApi(
  apiItems: IApiSolicitationItem[],
  items: INotificationCenterSolicitationItem[],
  seenIds: Set<string>,
  establishmentByExternalId: Map<number, { establishmentId: number; establishmentName: string }>,
  destinationProviders: IDestinationProvider[],
  ticId: number | null
): void {
  apiItems.forEach((apiItem) => {
    if (!isEligibleNotificationCenterSolicitation(apiItem)) {
      return
    }

    const solicitation = normalizeApiSolicitationToItem(apiItem)
    const externalId = Number(solicitation.provider_external_id ?? solicitation.provider?.id)

    if (!Number.isFinite(externalId)) {
      return
    }

    const resolvedContext = resolveSolicitationCenterContext(
      solicitation,
      externalId,
      establishmentByExternalId,
      destinationProviders,
      ticId
    )

    if (!resolvedContext) {
      return
    }

    const centerItem = mapDestinationSolicitationToCenterItem(
      solicitation,
      resolvedContext.establishmentId,
      resolvedContext.establishmentName
    )

    if (seenIds.has(centerItem.id)) {
      return
    }

    seenIds.add(centerItem.id)
    items.push(centerItem)
  })
}

async function appendDialogueSolicitationCenterItems(
  ticIds: number[],
  items: INotificationCenterSolicitationItem[],
  seenIds: Set<string>,
  establishmentByExternalId: Map<number, { establishmentId: number; establishmentName: string }>,
  destinationProviders: IDestinationProvider[]
): Promise<void> {
  await Promise.all(
    ticIds.map(async (ticId) => {
      try {
        const response = await LocalDiffuserDataService.getDialogueSolicitations(ticId)
        const solicitations = (response.data.data?.solicitations ?? []) as IApiSolicitationItem[]

        appendSolicitationCenterItemsFromApi(
          solicitations,
          items,
          seenIds,
          establishmentByExternalId,
          destinationProviders,
          ticId
        )
      } catch {
        // Hubo indisponible pour l'OT : on ignore cette source.
      }
    })
  )
}

export async function fetchSolicitationCenterItems(
  context: SolicitationCenterFetchContext
): Promise<INotificationCenterSolicitationItem[]> {
  const items: INotificationCenterSolicitationItem[] = []
  const seenIds = new Set<string>()
  const establishmentStore = useEstablishmentStore()
  const establishments = [...context.establishments]
  const destinationProviders = context.destinationProviders ?? []
  const ticId = context.ticId ?? null
  const isDialogue = context.isDialogue === true

  const establishmentsById = new Map<number, IEstablishment>()
  establishments.forEach((establishment) => {
    establishmentsById.set(establishment.id, establishment)
  })

  const currentEstablishment = establishmentStore.currentEstablishment
  if (currentEstablishment?.id && !establishmentsById.has(currentEstablishment.id)) {
    establishments.push(currentEstablishment)
    establishmentsById.set(currentEstablishment.id, currentEstablishment)
  }

  const establishmentByExternalId = buildEstablishmentByExternalIdMap(establishments)

  if (isDialogue) {
    const dialogueTicIds =
      context.ticIds && context.ticIds.length > 0
        ? context.ticIds
        : ticId != null
          ? [ticId]
          : []

    if (dialogueTicIds.length === 0) {
      return items
    }

    await appendDialogueSolicitationCenterItems(
      dialogueTicIds,
      items,
      seenIds,
      establishmentByExternalId,
      destinationProviders
    )

    return items
  }

  if (ticId != null) {
    await appendDialogueSolicitationCenterItems(
      [ticId],
      items,
      seenIds,
      establishmentByExternalId,
      destinationProviders
    )

    return items
  }

  const externalIds = collectSolicitationExternalIds(establishments, destinationProviders, ticId)

  const requests = externalIds.map((externalId) =>
    LocalDiffuserDataService.getLocalDiffuserSolicitations(externalId)
      .then((response) => {
        const solicitations = (response.data.data?.solicitations ?? []) as IApiSolicitationItem[]

        appendSolicitationCenterItemsFromApi(
          solicitations.map((apiItem) => ({
            ...apiItem,
            provider_external_id: apiItem.provider_external_id ?? externalId,
          })),
          items,
          seenIds,
          establishmentByExternalId,
          destinationProviders,
          ticId
        )
      })
      .catch(() => {
        // Hubo indisponible pour ce diffuseur : on ignore cette source.
      })
  )

  await Promise.all(requests)
  return items
}
